import { formatDecimal, tt, type CourierQuote } from '@agos/shared'

import type { DeliveryZone, ZoneOutcome } from './delivery'
import type { PaymentSettings } from './payment-methods'

/**
 * Delivery in a restaurant where external couriers deliver - `FRONTEND.md` §9.786, on BACKEND.md §10A.438 B, owner
 * O139.
 *
 * The owner: *"prevádzka nemá šoférov ... doprava sa počíta z odmeny šoféra, napr. 20 € + 4,50 € doprava"*. In that
 * mode the fee is not a zone's tier but the courier's reward for the distance, which the server quotes from the road
 * distance to the address (`GET /delivery/quote`). The order is priced by the same rule when it is placed, so the
 * quote is what the customer pays.
 *
 * **The answer is a `ZoneOutcome`**, the shape the checkout already draws, submits and refuses on: a quote becomes
 * `ok` with the distance as its line, a short basket `below-minimum`, a far address `outside` with the distance said,
 * a failed quote `unreadable`. One shape means the page has one set of rules for *may this order be placed*, which
 * `submit-block.test.ts` holds - two would be two answers to one question.
 */

/* The quote reader is `@agos/shared`'s since the till needed it too (§9.786); re-exported so this module's callers
   are untouched by where it lives. */
export {
  courierQuoteOf,
  DELIVERY_QUOTE_PATH,
  quotePath,
  type CourierQuote,
} from '@agos/shared'

/**
 * The line the checkout draws in place of a zone: the distance, which is what the courier is paid for. Built as a
 * `DeliveryZone` so `zoneLine` and `basketProgress` read it unchanged - no tiers, so no *cheaper delivery* is
 * promised, and the minimum is the courier minimum.
 */
export function courierRun(distanceKm: number | null, minimumCents: number): DeliveryZone {
  return {
    id: 'courier',
    name: distanceKm === null ? tt('Doručenie') : `${tt('Doručenie')} ${formatDecimal(distanceKm, 1)} km`,
    enabled: true,
    type: 'courier',
    center: null,
    radius: null,
    polygon: null,
    minOrder: minimumCents / 100,
    tiers: [],
  }
}

/**
 * What delivery to this point costs where couriers deliver, as the checkout's own outcome.
 *
 * @param quote the answer for this point, or null while it is asked or when it failed
 * @param failed whether the quote for this point failed - said as `unreadable`, and the order still offered (§9.545's
 *     decision: the server prices and refuses at placing either way)
 */
export function courierDeliveryFor(
  point: { lat: number; lng: number } | null,
  quote: CourierQuote | null,
  subtotalCents: number,
  failed: boolean,
): ZoneOutcome {
  if (point === null) return { kind: 'unknown' }
  if (failed) return { kind: 'unreadable' }
  if (quote === null || quote.feeCents === null) return { kind: 'unknown' }
  if (quote.withinRange === false) {
    return {
      kind: 'outside',
      reach: quote.km === null || quote.maxKm === null ? undefined : { km: quote.km, maxKm: quote.maxKm },
    }
  }
  const run = courierRun(quote.km, quote.minimumCents)
  if (quote.minimumCents > 0 && subtotalCents < quote.minimumCents) {
    return { kind: 'below-minimum', zone: run, minimumCents: quote.minimumCents }
  }
  return { kind: 'ok', zone: run, feeCents: quote.feeCents }
}

/**
 * The payment settings a courier delivery can honour - the owner's answer 4: *cash, card online, or the QR at the
 * door*. An external courier carries no terminal, so *Karta - terminál* is not offered on a delivery, and the server
 * refuses it there too (`OrderCreationService.refuseCardAtTheDoor`). A pickup keeps it.
 */
export function courierPaymentSettings(settings: PaymentSettings, courierDelivery: boolean): PaymentSettings {
  return courierDelivery ? { ...settings, cardTerminalOffered: false } : settings
}
